import React, { useState, useMemo } from 'react';
import {
    Box, Grid, Typography, Divider, Paper, Stack,
    TextField, InputAdornment, Button, Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import AddToPhotosRoundedIcon from '@mui/icons-material/AddToPhotosRounded';
import Back from '../../../common/components/BackNavigateComponent';
import { useGroupList, useModuleList, useUserList } from '../../../common/API/hooks';
import ModuleTable from './ModuleTable';
import ModuleDrawer from './ModuleDrawer';
import UnregisteredModules from './UnregisteredModules';
import {
    inferModuleGroup,
    getModuleGroupLabel,
    getMajorGroupKey,
    getMajorGroupMeta,
} from './moduleGrouping';
import Fuse from 'fuse.js';
import { useDebounce } from 'use-debounce';

const BRAND = '#0F2FEB';

function StatCard({ icon, label, value, color, bgColor, loading }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: bgColor, color, display: 'flex' }}>
                    {React.cloneElement(icon, { fontSize: 'medium' })}
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color={color}>
                        {loading ? '—' : value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

export default function ModulePermission() {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebounce(searchQuery, 300);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedMajorGroup, setSelectedMajorGroup] = useState(null);
    const [selectedDashboardSubGroup, setSelectedDashboardSubGroup] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('add');
    const [selectedModule, setSelectedModule] = useState(null);

    const { data: moduleList, isLoading: moduleLoading } = useModuleList();
    const { data: groupList, isLoading: groupLoading } = useGroupList();
    const { data: userList, isLoading: userLoading } = useUserList();

    const stats = useMemo(() => {
        if (!moduleList) return { total: 0, publicCount: 0, privateCount: 0 };
        const filtered = moduleList.filter(m => !m.name?.startsWith('TRX:'));
        return {
            total: filtered.length,
            publicCount: filtered.filter(m => m.is_public).length,
            privateCount: filtered.filter(m => !m.is_public).length,
        };
    }, [moduleList]);

    const userOptions = useMemo(() => {
        if (userLoading || !userList) return [];
        return userList.map(u => ({ label: u.username, value: u.username }));
    }, [userList, userLoading]);

    // Compute effective users per module (combining group members + direct users)
    const modulesWithVisibility = useMemo(() => {
        if (!moduleList) return [];
        const filteredList = moduleList.filter(m => !m.name?.startsWith('TRX:'));
        if (!groupList) {
            return filteredList.map(m => {
                const moduleGroup = inferModuleGroup(m.name);
                const majorGroup = getMajorGroupKey(moduleGroup);
                const majorGroupMeta = getMajorGroupMeta(moduleGroup);
                return {
                    ...m,
                    moduleGroup,
                    moduleGroupLabel: getModuleGroupLabel(moduleGroup),
                    majorGroup,
                    majorGroupLabel: majorGroupMeta.label,
                    visibleToUsers: m.allowed_users,
                };
            });
        }
        return filteredList.map(m => {
            const usersFromGroups = groupList
                .filter(g => m.allowed_groups?.includes(g.name))
                .flatMap(g => g.users);
            const visibleToUsers = [...new Set([...usersFromGroups, ...(m.allowed_users || [])])].sort();
            const moduleGroup = inferModuleGroup(m.name);
            const majorGroup = getMajorGroupKey(moduleGroup);
            const majorGroupMeta = getMajorGroupMeta(moduleGroup);
            return {
                ...m,
                moduleGroup,
                moduleGroupLabel: getModuleGroupLabel(moduleGroup),
                majorGroup,
                majorGroupLabel: majorGroupMeta.label,
                visibleToUsers,
            };
        });
    }, [moduleList, groupList]);

    // User filter
    const userFilteredModules = useMemo(() => {
        if (!selectedUser) return modulesWithVisibility;
        return modulesWithVisibility.filter(m => m.is_public || m.visibleToUsers.includes(selectedUser.value));
    }, [modulesWithVisibility, selectedUser]);

    const majorGroupOptions = useMemo(() => {
        const groupMap = new Map();
        modulesWithVisibility.forEach(moduleItem => {
            if (!groupMap.has(moduleItem.majorGroup)) {
                groupMap.set(moduleItem.majorGroup, {
                    value: moduleItem.majorGroup,
                    label: moduleItem.majorGroupLabel,
                });
            }
        });
        return [...groupMap.values()].sort((a, b) => a.label.localeCompare(b.label));
    }, [modulesWithVisibility]);

    const dashboardSubGroupOptions = useMemo(() => {
        const subgroupMap = new Map();
        userFilteredModules
            .filter(moduleItem => moduleItem.majorGroup === 'dashboard')
            .forEach(moduleItem => {
                if (!subgroupMap.has(moduleItem.moduleGroup)) {
                    subgroupMap.set(moduleItem.moduleGroup, {
                        value: moduleItem.moduleGroup,
                        label: moduleItem.moduleGroupLabel,
                    });
                }
            });
        return [...subgroupMap.values()].sort((a, b) => a.label.localeCompare(b.label));
    }, [userFilteredModules]);

    const userAndGroupFilteredModules = useMemo(() => {
        let next = userFilteredModules;
        if (selectedMajorGroup) {
            next = next.filter(moduleItem => moduleItem.majorGroup === selectedMajorGroup.value);
        }
        if (selectedDashboardSubGroup && selectedMajorGroup?.value === 'dashboard') {
            next = next.filter(moduleItem => moduleItem.moduleGroup === selectedDashboardSubGroup.value);
        }
        return next;
    }, [userFilteredModules, selectedMajorGroup, selectedDashboardSubGroup]);

    // Fuzzy search
    const filteredModules = useMemo(() => {
        if (!debouncedSearch) return userAndGroupFilteredModules;
        const fuse = new Fuse(userAndGroupFilteredModules, {
            keys: ['name', 'moduleGroupLabel', 'moduleGroup', 'majorGroupLabel', 'majorGroup'],
            threshold: 0.35,
        });
        return fuse.search(debouncedSearch).map(r => r.item);
    }, [userAndGroupFilteredModules, debouncedSearch]);

    const isLoading = moduleLoading || groupLoading;

    const handleAddModule = () => {
        setSelectedModule(null);
        setDrawerMode('add');
        setDrawerOpen(true);
    };

    const handleEditModule = (module) => {
        setSelectedModule(module);
        setDrawerMode('edit');
        setDrawerOpen(true);
    };

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 2 }}>
                <Back />
                <Typography variant="h5" fontWeight="bold" sx={{ ml: 1 }}>
                    Module Permission
                </Typography>
            </Stack>
            <Divider sx={{ borderBottom: 3, mb: 3 }} />

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<ExtensionRoundedIcon />} label="Total Modules" value={stats.total}
                        color={BRAND} bgColor="#EEF1FD" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<PublicRoundedIcon />} label="Public Modules" value={stats.publicCount}
                        color="#2E7D32" bgColor="#EDF7EE" loading={isLoading} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<LockRoundedIcon />} label="Restricted Modules" value={stats.privateCount}
                        color="#ED6C02" bgColor="#FFF4E5" loading={isLoading} />
                </Grid>
            </Grid>

            {/* Toolbar */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
                p: 1.5, bgcolor: '#F8F9FA',
            }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                    alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flex={1} alignItems={{ sm: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search modules by name…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            sx={{ bgcolor: 'white', minWidth: { sm: 280 }, width: { xs: '100%', sm: 'auto' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Autocomplete
                            size="small"
                            options={userOptions}
                            value={selectedUser}
                            onChange={(_, newVal) => setSelectedUser(newVal)}
                            loading={userLoading}
                            sx={{ bgcolor: 'white', minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Filter by user access…" />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                        />
                        <Autocomplete
                            size="small"
                            options={majorGroupOptions}
                            value={selectedMajorGroup}
                            onChange={(_, newVal) => {
                                setSelectedMajorGroup(newVal);
                                if (newVal?.value !== 'dashboard') {
                                    setSelectedDashboardSubGroup(null);
                                }
                            }}
                            sx={{ bgcolor: 'white', minWidth: { sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Filter by major group..." />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                        />
                        <Autocomplete
                            size="small"
                            options={dashboardSubGroupOptions}
                            value={selectedDashboardSubGroup}
                            onChange={(_, newVal) => setSelectedDashboardSubGroup(newVal)}
                            disabled={selectedMajorGroup?.value !== 'dashboard'}
                            sx={{ bgcolor: 'white', minWidth: { sm: 240 }, width: { xs: '100%', sm: 'auto' } }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Filter dashboard subgroup..." />
                            )}
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                        />
                    </Stack>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                        startIcon={<AddToPhotosRoundedIcon />}
                        onClick={handleAddModule}
                    >
                        Add Module
                    </Button>
                </Stack>
            </Paper>

            {/* Results count */}
            {!isLoading && moduleList && (
                <Box sx={{ px: 2, py: 0.75, bgcolor: '#FAFAFA', borderLeft: '2px solid #E0E0E0', borderRight: '2px solid #E0E0E0' }}>
                    <Typography variant="caption" color="text.secondary">
                        {debouncedSearch || selectedUser
                            || selectedMajorGroup || selectedDashboardSubGroup
                            ? `Showing ${filteredModules.length} of ${modulesWithVisibility.length} modules`
                            : `${modulesWithVisibility.length} module${modulesWithVisibility.length !== 1 ? 's' : ''} total`}
                    </Typography>
                </Box>
            )}

            {/* Table */}
            <Paper variant="outlined" sx={{
                borderRadius: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 0,
            }}>
                <ModuleTable
                    filteredModules={filteredModules}
                    isLoading={isLoading}
                    onEditModule={handleEditModule}
                />
            </Paper>

            <Box sx={{ mt: 3 }}>
                <UnregisteredModules />
            </Box>

            {/* Drawer */}
            <ModuleDrawer
                isOpen={drawerOpen}
                mode={drawerMode}
                module={selectedModule}
                onClose={() => setDrawerOpen(false)}
            />
        </Box>
    );
}